// 書籍間の対話 AI 画面
// 既読本から 2 冊を選び、それぞれの著者が架空対話する形式の長文を生成する。
import {
  escapeHtml,
  screenHead,
  showScreen,
  showLoading,
  hideLoading,
  toast,
  renderHistorySection,
  bindHistoryHandlers,
} from '../ui.js';
import { fetchBookDialog, fetchAllNotesParallel } from '../api.js';
import { getAllBooks, upsertBooks, saveAiHistory, getAiHistoryByType } from '../cache.js';

export async function showBookDialogScreen() {
  const histItems = await getAiHistoryByType('book-dialog');
  await renderEntry(histItems);
}

async function renderEntry(histItems) {
  const books = await getAllBooks();
  // メモのある既読/読書中書籍を候補に
  const candidates = books
    .filter((b) => (b.status === '読了' || b.status === '読書中') && (b.note || '').trim().length > 80)
    .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));

  if (candidates.length < 2) {
    const screen = showScreen(
      'book-dialog',
      `${screenHead('書籍間の対話')}<p class="empty">メモのある既読書が 2 冊以上必要です（現在 ${candidates.length} 冊）。書籍メモを 80 字以上書いてからお試しください。</p>`
    );
    screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
    return;
  }

  const html = `
    ${screenHead('書籍間の対話')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 16px;">
      既読 2 冊を選ぶと、それぞれの著者が架空対話する形式で論点を浮かび上がらせます。<br/>
      「論題」を入れると対話の方向性を指定できます（任意）。
    </p>
    <div class="form-group">
      <label class="form-label">論題 <span style="color:var(--mute);font-size:11px;">任意</span></label>
      <input class="form-input" id="bd-topic" type="text" placeholder="例: 美と日常 / 写真と倫理 / 構造主義の限界" autocomplete="off" />
    </div>
    <div class="form-group">
      <label class="form-label">参加させる書籍 <span style="color:var(--mute);font-size:11px;">2 冊を選択</span></label>
      <ul class="bd-picker" id="bd-picker">
        ${candidates
          .map(
            (b) => `
          <li>
            <label class="bd-picker__row">
              <input type="checkbox" data-book-id="${escapeHtml(b.id)}" />
              <span class="bd-picker__title">${escapeHtml(b.name)}</span>
              <span class="bd-picker__author">${escapeHtml(b.author || '')}</span>
            </label>
          </li>
        `
          )
          .join('')}
      </ul>
    </div>
    <button class="btn btn--ink btn--block" id="btn-go" type="button">
      <svg class="icon"><use href="#i-chat"/></svg>
      <span>対話を生成する</span>
    </button>
    <p style="margin-top:14px;font-size:11px;color:var(--mute);line-height:1.6;font-family:var(--font-mono);letter-spacing:0.08em;">
      使用モデル: claude-opus-5-5 · 1 回あたり 約 6-15 円
    </p>
    ${renderHistorySection(histItems)}
  `;
  const screen = showScreen('book-dialog', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());

  // チェックボックスの max 2 制御
  screen.querySelectorAll('[data-book-id]').forEach((cb) => {
    cb.addEventListener('change', () => {
      const checked = screen.querySelectorAll('[data-book-id]:checked');
      if (checked.length > 2) {
        cb.checked = false;
        toast('2 冊までです');
      }
    });
  });

  screen.querySelector('#btn-go').addEventListener('click', async () => {
    const ids = Array.from(screen.querySelectorAll('[data-book-id]:checked')).map((c) => c.dataset.bookId);
    if (ids.length !== 2) {
      toast('参加させる書籍を 2 冊選んでください');
      return;
    }
    const topic = screen.querySelector('#bd-topic').value.trim();
    await runDialog(ids, topic, candidates);
  });

  bindHistoryHandlers(
    screen,
    histItems,
    (entry) => renderResult(entry.payload, entry.context?.topic || ''),
    () => showBookDialogScreen()
  );
}

async function runDialog(ids, topic, candidates) {
  const selected = candidates.filter((b) => ids.includes(b.id));
  // メモ補完（念のため）
  const missing = selected.filter((b) => !b.note).length;
  if (missing > 0) {
    showLoading(`書籍メモを補完中… (0/${missing})`);
    await fetchAllNotesParallel(selected, (done, total) => {
      const ov = document.getElementById('overlay-msg');
      if (ov) ov.textContent = `書籍メモを補完中… (${done}/${total})`;
    });
    await upsertBooks(selected);
    hideLoading();
  }
  showLoading('AI が対話を執筆中…');
  try {
    const payload = selected.map((b) => ({
      name: b.name,
      author: b.author,
      genre: b.genre,
      note: b.note,
    }));
    const result = await fetchBookDialog(payload, topic);
    await saveAiHistory({
      type: 'book-dialog',
      title: `${selected[0].name} × ${selected[1].name}`,
      subtitle: topic || '',
      context: { ids, topic },
      payload: result,
    });
    renderResult(result, topic);
  } catch (e) {
    console.error(e);
    toast('対話の生成に失敗しました');
  } finally {
    hideLoading();
  }
}

export function renderFromHistory(entry) {
  renderResult(entry.payload, entry.context?.topic || '');
}

function renderResult(data, topic) {
  const lines = data.dialogue || [];
  const html = `
    ${screenHead('書籍間の対話')}
    ${
      topic
        ? `<div style="margin:0 0 16px;padding:10px 14px;border:1px solid var(--line);">
             <p style="margin:0 0 2px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">topic</p>
             <p style="margin:0;font-family:var(--font-serif);font-size:14px;">${escapeHtml(topic)}</p>
           </div>`
        : ''
    }
    <ul class="dialog-list">
      ${lines
        .map(
          (l) => `
        <li class="dialog-turn">
          <p class="dialog-turn__speaker">${escapeHtml(l.speaker || '')}</p>
          <p class="dialog-turn__line">${escapeHtml(l.line || '')}</p>
        </li>
      `
        )
        .join('')}
    </ul>
    ${
      data.synthesis
        ? `<section class="dialog-synthesis">
             <p class="section-label">synthesis</p>
             <p style="margin:0;font-family:var(--font-serif);font-size:14px;line-height:1.85;color:var(--dim);">${escapeHtml(data.synthesis)}</p>
           </section>`
        : ''
    }
    <button class="btn btn--block btn--ghost" id="btn-regen" type="button" style="margin-top:24px;">
      <svg class="icon"><use href="#i-refresh"/></svg>
      <span>別の組み合わせで生成する</span>
    </button>
  `;
  const screen = showScreen('book-dialog', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-regen').addEventListener('click', () => showBookDialogScreen());
}
